import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Search,
  ArrowLeft,
  Upload,
  Trash2,
  Eye,
  EyeOff,
  Save,
  ExternalLink,
} from "lucide-react";
import { api, formatApiErrorDetail, API } from "@/lib/api";

const inputCls =
  "w-full bg-cream border border-sand rounded-xl px-3.5 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1.5";

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // post object or {} for new
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blog");
      setPosts(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = posts.filter(
    (p) => !q || (p.title || "").toLowerCase().includes(q.toLowerCase())
  );

  if (editing) {
    return (
      <BlogEditor
        post={editing}
        onBack={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="blog-workspace">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Journal</div>
          <h2 className="font-serif text-3xl text-ink">Write a post for the website.</h2>
          <p className="text-sm text-ink-soft mt-1">{posts.length} total · {posts.filter((p) => p.status === "published").length} live · {posts.filter((p) => p.status === "draft").length} drafts.</p>
        </div>
        <button
          onClick={() => setEditing({})}
          data-testid="blog-new-button"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors"
        >
          <Plus size={14} /> New post
        </button>
      </div>

      <div className="relative max-w-md mb-5">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search posts…"
          className="w-full bg-cream border border-sand/70 rounded-full pl-9 pr-4 py-2 text-sm text-ink focus:outline-none focus:border-navy"
        />
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center text-ink-muted">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-ink-soft italic border border-dashed border-sand rounded-2xl">
          No posts yet. Click "New post" to write your first one.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => setEditing(p)}
              data-testid={`blog-row-${p.id}`}
              className="w-full text-left bg-cream-soft border border-sand/70 rounded-2xl px-5 py-4 hover:border-navy/40 transition-colors flex flex-wrap items-center justify-between gap-3"
            >
              <div className="flex items-center gap-4 min-w-0">
                {p.cover_image_path ? (
                  <img
                    src={`${API}/admin/blog-cover/${p.id}?auth=${encodeURIComponent(localStorage.getItem("ams_token") || "")}`}
                    alt=""
                    className="h-14 w-20 rounded-lg object-cover border border-sand/60"
                  />
                ) : (
                  <div className="h-14 w-20 rounded-lg bg-cream border border-sand/60 flex items-center justify-center text-[10px] uppercase tracking-[0.18em] text-ink-soft">No cover</div>
                )}
                <div className="min-w-0">
                  <div className="font-serif text-lg text-ink truncate">{p.title}</div>
                  <div className="text-xs text-ink-soft mt-0.5 truncate">
                    /blog/{p.slug} · updated {format(new Date(p.updated_at), "d MMM")}
                  </div>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full ${p.status === "published" ? "bg-navy/10 text-navy" : "bg-cream border border-sand text-ink-soft"}`}>
                {p.status === "published" ? <Eye size={11} /> : <EyeOff size={11} />}
                {p.status}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BlogEditor({ post, onBack, onSaved }) {
  const isNew = !post.id;
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title: post.title || "",
    slug: post.slug || "",
    excerpt: post.excerpt || "",
    body: post.body || "",
    status: post.status || "draft",
  });
  const [postId, setPostId] = useState(post.id || null);
  const [coverPath, setCoverPath] = useState(post.cover_image_path || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const save = async (newStatus) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (newStatus) payload.status = newStatus;
      if (postId) {
        const r = await api.patch(`/admin/blog/${postId}`, payload);
        setForm({ ...form, slug: r.data.slug, status: r.data.status });
        setCoverPath(r.data.cover_image_path);
        toast.success(payload.status === "published" ? "Published" : "Saved");
      } else {
        const r = await api.post("/admin/blog", payload);
        setPostId(r.data.id);
        setForm({ ...form, slug: r.data.slug, status: r.data.status });
        toast.success("Post created");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!postId) {
      toast.error("Save the post first, then upload a cover.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/blog/${postId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCoverPath(r.data.cover_image_path);
      toast.success("Cover image saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deletePost = async () => {
    if (!postId) return onBack();
    if (!window.confirm("Delete this post permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/blog/${postId}`);
      toast.success("Post deleted");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const token = typeof window !== "undefined" ? localStorage.getItem("ams_token") : "";
  const coverUrl = postId && coverPath
    ? `${API}/admin/blog-cover/${postId}?auth=${encodeURIComponent(token || "")}`
    : null;

  return (
    <div data-testid="blog-editor">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-navy mb-6">
        <ArrowLeft size={14} /> Back to all posts
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">{isNew ? "New post" : "Editing"}</div>
          <h2 className="font-serif text-3xl text-ink">{form.title || "Untitled post"}</h2>
          {form.slug && (
            <div className="text-xs text-ink-soft mt-1">
              /blog/{form.slug}
              {form.status === "published" && (
                <a href={`/blog/${form.slug}`} target="_blank" rel="noopener noreferrer" className="ml-2 text-navy hover:underline inline-flex items-center gap-1">
                  view <ExternalLink size={10} />
                </a>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => save()}
            disabled={saving || !form.title}
            data-testid="blog-save-draft"
            className="inline-flex items-center gap-1.5 rounded-full bg-cream text-ink border border-sand hover:border-navy/40 px-4 py-2 text-xs font-medium transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Save draft
          </button>
          <button
            type="button"
            onClick={() => save("published")}
            disabled={saving || !form.title}
            data-testid="blog-publish"
            className="inline-flex items-center gap-1.5 rounded-full bg-navy text-cream px-4 py-2 text-xs font-medium hover:bg-navy-deep transition-colors disabled:opacity-50"
          >
            <Eye size={12} /> {form.status === "published" ? "Update live post" : "Publish"}
          </button>
          {!isNew && (
            <button
              type="button"
              onClick={deletePost}
              className="inline-flex items-center gap-1.5 rounded-full bg-cream text-ink-soft border border-sand hover:border-red-300 hover:text-red-700 px-4 py-2 text-xs font-medium transition-colors"
            >
              <Trash2 size={12} /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
            <label className={labelCls}>Title</label>
            <input
              className={`${inputCls} text-lg font-serif`}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Title of your post"
              required
              data-testid="blog-title-input"
            />
            <div className="mt-4">
              <label className={labelCls}>Short summary (shown on the journal index)</label>
              <textarea
                rows={2}
                className={`${inputCls} resize-y`}
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="One or two sentences that give a taste of the post."
              />
            </div>
          </div>

          <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls + " mb-0"}>Body</label>
              <div className="text-[10px] text-ink-soft italic">Empty line = paragraph. Start a line with <code className="bg-cream px-1 rounded">## </code> for heading, <code className="bg-cream px-1 rounded">&gt; </code> for quote.</div>
            </div>
            <textarea
              rows={22}
              className={`${inputCls} resize-y font-serif text-[16px] leading-[1.7]`}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Write here… as much as you like."
              data-testid="blog-body-input"
            />
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
            <label className={labelCls}>Cover image</label>
            {coverUrl ? (
              <div className="space-y-3">
                <img src={coverUrl} alt="cover" className="w-full rounded-xl aspect-[16/9] object-cover border border-sand/60" />
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={uploadCover}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-cream border border-sand/80 hover:border-navy/40 px-3 py-2 text-xs text-ink-soft transition-colors disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />} Replace cover
                </button>
              </div>
            ) : !postId ? (
              <div className="text-xs text-ink-soft italic">Save the post first, then you can upload a cover.</div>
            ) : (
              <div className="space-y-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={uploadCover}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="blog-cover-upload"
                  className="w-full inline-flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-sand py-10 text-ink-soft hover:border-navy/40 transition-colors disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                  <span className="text-xs">Click to upload cover image</span>
                  <span className="text-[10px] text-ink-soft">JPG · PNG · WEBP · up to 8MB</span>
                </button>
              </div>
            )}
          </div>

          <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
            <label className={labelCls}>URL slug</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-soft">/blog/</span>
              <input
                className={`${inputCls} text-xs`}
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="auto-from-title"
              />
            </div>
            <p className="text-[10px] text-ink-soft mt-2 italic">Auto-filled from the title — change only if you have a reason.</p>
          </div>

          <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
            <label className={labelCls}>Status</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, status: "draft" })}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium transition-colors ${form.status === "draft" ? "bg-navy text-cream" : "bg-cream text-ink-soft border border-sand hover:border-navy/40"}`}
              >
                <EyeOff size={12} /> Draft
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, status: "published" })}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium transition-colors ${form.status === "published" ? "bg-navy text-cream" : "bg-cream text-ink-soft border border-sand hover:border-navy/40"}`}
              >
                <Eye size={12} /> Published
              </button>
            </div>
            <p className="text-[10px] text-ink-soft mt-2 italic">Drafts aren't visible on your website. Use Save draft above to commit any change.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
